import { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import { setActiveTriggerMessage, setAnimateWidget, setShowLuancher } from 'app/redux/widget/widget.actions'
import {
  CLOSE_WIDGET,
  SHOW_AUTO_MSG_IN_GREETING_DIALOGUE,
  OPEN_WIDGET,
  SPIN_LAUNCHER_TRIGGER_EVENT,
  OPEN_WIDGET_AND_SEND_MESSAGE,
} from 'app/constants/autoMsgTriggerEvents'
import { areUrlsEqual, getHostWebsiteUrl } from 'app/utils/links'
import { setNewMessage, performAutoMsgOpenWidgetAction } from 'app/redux/messages/messages.actions'
import { LAUNCHER_CONTENT_STATE } from 'app/constants/launcherContentStates'

const getDefaultTeam = ({ messagesData }) => messagesData.defaultTeam
const getFaqs = ({ widgetData }) => widgetData.widgetInfo.faq
const getDepartments = ({ widgetData }) => widgetData.widgetInfo.data?.teams
const getAllAutoMessages = ({ widgetData }) => widgetData.widgetInfo.data?.trigger.messages

function useAutoMessages() {
  const [triggersChecked, setTriggersChecked] = useState(false)

  const defaultTeam = useSelector(getDefaultTeam)
  const departments = useSelector(getDepartments)
  const faqs = useSelector(getFaqs)
  const allAutoMessages = useSelector(getAllAutoMessages)
  const dispatch = useDispatch()

  /**
   * this function checks specified equality condition for 'title' or 'url' of a website
   *
   * @param {string} value - condition value
   * @param {string} input - it can be url or page title of a website
   * @param {string} eqCondition - equality condition
   * @param {boolean} isUrlCheck - checks equality for urls
   */
  const doesEqualityConditionApply = (value, input, eqCondition, isUrlCheck) => {
    const sanitizedInput = String(input || '')

    const urlEqualityObj = {
      '==': isUrlCheck ? areUrlsEqual(value, input) : value == input,
      '!=': isUrlCheck ? !areUrlsEqual(value, input) : value !== input,
      contains: sanitizedInput.includes(value),
      starts: sanitizedInput.startsWith(value),
      end: sanitizedInput.endsWith(value),
      ncontains: !sanitizedInput.includes(value),
    }

    return urlEqualityObj[eqCondition]
  }

  const doesConditionApply = (condition) => {
    let pageTitle = window.parent.document.querySelector('title')?.innerHTML || ''

    const websiteUrl = getHostWebsiteUrl()

    // 1 => time after load
    // 2 => page address
    // 3 => page title
    const conditionType = condition.cond
    const conditionValue = condition.value
    const equalityCondition = condition.eq

    if (conditionType === '2') {
      return doesEqualityConditionApply(conditionValue, websiteUrl, equalityCondition, true)
    }

    if (conditionType === '3') {
      return doesEqualityConditionApply(conditionValue, pageTitle, equalityCondition)
    }
  }

  const showMessageAfterTime = (message, time) => {
    setTimeout(() => {
      showAutoMessage(message)
    }, time)
  }

  const getTriggerMessage = (activeTriggerMessages) => {
    activeTriggerMessages.forEach((message) => {
      const conditions = message.conditions
      if (!conditions) return

      // filtering time conditions
      const timeConditions = conditions.filter((item) => +item.cond === 1).map((item) => +item.value)
      const secondsToTrigger = timeConditions.length ? Math.min(...[timeConditions]) : 1
      const nonTimeConditionsNum = conditions.length - timeConditions.length

      const appliedConditionsNum = conditions.reduce((prev, cond) => {
        if (doesConditionApply(cond)) return prev + 1

        return prev
      }, 0)

      const isLeastConditionMet =
        message.numberOfConditions === 'one' && Math.max(appliedConditionsNum, timeConditions.length)
      const allConditionMet = message.numberOfConditions === 'all' && appliedConditionsNum === nonTimeConditionsNum

      // checks if all or one (depending on what user choosed) conditions are true and therefore show message
      if (isLeastConditionMet || allConditionMet) {
        showMessageAfterTime(message, secondsToTrigger * 1000)
      }
    })
  }

  const showAutoMessage = (message) => {
    switch (message.commands) {
      case SPIN_LAUNCHER_TRIGGER_EVENT:
        dispatch(
          setAnimateWidget({
            status: true,
            animationType: 'flip',
          }),
        )
        break

      case OPEN_WIDGET: {
        const launcherContentState =
          faqs?.length || departments?.length > 1
            ? LAUNCHER_CONTENT_STATE.MAIN_COVER
            : LAUNCHER_CONTENT_STATE.FIRST_CHAT

        dispatch(performAutoMsgOpenWidgetAction({ launcherContentState, teamId: defaultTeam._id }))

        break
      }

      case CLOSE_WIDGET:
        dispatch(setShowLuancher(false))
        break

      case SHOW_AUTO_MSG_IN_GREETING_DIALOGUE:
        dispatch(
          setActiveTriggerMessage({
            ...message,
            message: message.message,
            agent_id: null,
            avatar: null,
            title: message.name,
            isAutoMessage: true,
          }),
        )

        dispatch(
          setNewMessage({
            message: {
              me: false,
              avatar: null,
              sender: message.sender,
              type: 'text',
              content: message.message,
              isAutoMessage: true,
              createdAt: new Date(),
            },
            teamId: defaultTeam._id,
          }),
        )
        break

      case OPEN_WIDGET_AND_SEND_MESSAGE:
        dispatch(
          performAutoMsgOpenWidgetAction({
            launcherContentState: LAUNCHER_CONTENT_STATE.FIRST_CHAT,
            teamId: defaultTeam._id,
          }),
        )

        dispatch(
          setNewMessage({
            message: {
              me: false,
              avatar: null,
              type: 'text',
              content: message.message,
              isAutoMessage: true,
              createdAt: new Date(),
            },
            teamId: defaultTeam._id,
          }),
        )
        break

      default:
        break
    }
  }

  useEffect(() => {
    if (allAutoMessages && defaultTeam && !triggersChecked) {
      getTriggerMessage(allAutoMessages)
      setTriggersChecked(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allAutoMessages, defaultTeam])
}

export default useAutoMessages
